import { IMealPeriod } from "@dining/api-client";
import {
  Body1,
  Button,
  Dropdown,
  Input,
  Link,
  Option,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Title3,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  CAFE_ID_MISSING_ERROR,
  ERROR_PARSING_TIME,
  FAILED_TO_UPDATE_HOURS,
  validTimes,
} from "../../common/commonConstants";
import * as diningReducer from "../../reducers/diningReducer";
import * as diningSelector from "../../selectors/diningSelector";
import { useAppDispatch } from "../../store/store";
import styles from "./hoursOfOperation.module.css";
const useStyles = makeStyles({
  header: {
    display: "flex",
    alignItems: "center",
  },
  addButton: {
    textAlign: "right",
    marginTop: "10px",
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: tokens.spacingHorizontalM,
    marginTop: tokens.spacingVerticalL,
  },
  table: {
    marginTop: tokens.spacingVerticalXXL,
  },
  fixedWidthInput: {
    width: "180px",
    minWidth: "180px",
    maxWidth: "180px",
  },
});

const HoursOfOperation: React.FC = () => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const selectedSyncCafe = useSelector(diningSelector.syncCafeSelector);

  const [isEditing, setIsEditing] = useState(false);
  const [mealPeriods, setMealPeriods] = useState<IMealPeriod[]>([]);
  const [isSaveEnabled, setIsSaveEnabled] = useState(false);
  const [ariaLiveMessage, setAriaLiveMessage] = useState("");

  /* ---------------- Time Utilities (kept inside component) ---------------- */

  const extractTimeFromISO = (iso?: string) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return `${d.getUTCHours().toString().padStart(2, "0")}:${d.getUTCMinutes().toString().padStart(2, "0")}`;
    } catch (e) {
      console.error(ERROR_PARSING_TIME, e);
      return "";
    }
  };

  const formatTimeToAMPM = (time?: string) => {
    if (!time) return "";
    const [h, m] = time.split(":").map(Number);
    const period = h >= 12 ? "PM" : "AM";
    const hour = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${hour}:${m.toString().padStart(2, "0")} ${period}`;
  };

  /* ---------------- Derived Data ---------------- */

  const timeOptions = useMemo(
    () =>
      validTimes.map((t) => ({
        key: t,
        text: formatTimeToAMPM(t),
      })),
    [],
  );

  const getDropdownDisplayValue = (value?: string) => timeOptions.find((o) => o.key === value)?.text ?? "";

  /* ---------------- Effects ---------------- */

  useEffect(() => {
    if (!selectedSyncCafe?.mealPeriods) return;

    setMealPeriods(
      selectedSyncCafe.mealPeriods.map((p) => ({
        ...p,
        startsOn: extractTimeFromISO(p.startsOn),
        endsOn: extractTimeFromISO(p.endsOn),
      })),
    );
  }, [selectedSyncCafe]);

  useEffect(() => {
    if (!selectedSyncCafe?.mealPeriods) {
      setIsSaveEnabled(mealPeriods.length > 0);
      return;
    }

    const modified =
      mealPeriods.length !== selectedSyncCafe.mealPeriods.length ||
      mealPeriods.some((p, i) => {
        const o = selectedSyncCafe.mealPeriods?.[i];
        return (
          p.name !== o?.name ||
          p.startsOn !== extractTimeFromISO(o?.startsOn) ||
          p.endsOn !== extractTimeFromISO(o?.endsOn)
        );
      });

    setIsSaveEnabled(modified);
  }, [mealPeriods, selectedSyncCafe]);

  /* ---------------- Handlers ---------------- */

  const updatePeriod = (index: number, field: keyof IMealPeriod, value: string) => {
    setMealPeriods((prev) => prev.map((p, i) => (i === index ? { ...p, [field]: value } : p)));
    setIsSaveEnabled(true);
  };

  const addMealPeriod = () => {
    setMealPeriods((prev) => [...prev, { id: "", name: "", startsOn: "", endsOn: "", timeStamp: "" }]);
    setIsSaveEnabled(true);
    setAriaLiveMessage("Meal period added");
  };

  const removeMealPeriod = (index: number) => {
    setMealPeriods((prev) => prev.filter((_, i) => i !== index));
    setIsSaveEnabled(true);
    setAriaLiveMessage("Meal period removed");
  };
  const handleEdit = () => {
    setIsEditing(true);
    setIsSaveEnabled(false); // Reset Save button to disabled when entering edit mode
    setAriaLiveMessage("Hours of Operation Edit Page");
  };

  // Announce when Edit link receives focus
  const handleEditLinkFocus = () => {
    setAriaLiveMessage("Hours of Operation Edit");
  };
  const handleSave = async () => {
    if (!selectedSyncCafe?.id) {
      console.error(CAFE_ID_MISSING_ERROR);
      return;
    }

    try {
      const formatted = mealPeriods.map((p) => ({
        ...p,
        startsOn: p.startsOn ? new Date(Date.UTC(1, 0, 1, ...p.startsOn.split(":").map(Number))).toISOString() : "",
        endsOn: p.endsOn ? new Date(Date.UTC(1, 0, 1, ...p.endsOn.split(":").map(Number))).toISOString() : "",
      }));

      await dispatch(diningReducer.updateCafeHoursOfOperation(selectedSyncCafe.id, formatted));

      setIsEditing(false);
      setAriaLiveMessage("Meal periods saved successfully");
    } catch (e) {
      console.error(FAILED_TO_UPDATE_HOURS, e);
    }
  };

  const handleCancel = () => {
    if (selectedSyncCafe?.mealPeriods) {
      setMealPeriods(
        selectedSyncCafe.mealPeriods.map((p) => ({
          ...p,
          startsOn: extractTimeFromISO(p.startsOn),
          endsOn: extractTimeFromISO(p.endsOn),
        })),
      );
    }
    setIsEditing(false);
    setIsSaveEnabled(false);
  };

  /* ---------------- Render ---------------- */

  return (
    <div>
      <div className={classes.header}>
        <Title3 as="h2">
          Update hours of operation{" "}
          {!isEditing && (
            <sup>
              <Link
                className={styles.EditHours}
                data-testid="edit-hours-link"
                onClick={(event) => {
                  event.preventDefault();
                  handleEdit();
                }}
                onFocus={handleEditLinkFocus}
              >
                {" "}
                Edit
              </Link>
            </sup>
          )}
        </Title3>
      </div>
      <div className={styles.rightContent}>
        <Button className={classes.addButton} disabled={!isEditing} onClick={addMealPeriod}>
          Add new meal period
        </Button>
      </div>
      <div aria-live="assertive" style={{ position: "absolute", left: -9999 }}>
        {ariaLiveMessage}
      </div>

      {mealPeriods.length === 0 && !isEditing ? (
        <Body1>No meal periods are currently set for this cafe.</Body1>
      ) : (
        <Table className={classes.table} aria-label="Meal periods">
          <TableHeader>
            <TableRow style={{ marginTop: "40px", marginBottom: "20px", fontWeight: "600" }}>
              <TableHeaderCell>Meal period name</TableHeaderCell>
              <TableHeaderCell align="center">Meal period begins</TableHeaderCell>
              <TableHeaderCell align="center">Meal period ends</TableHeaderCell>
              <TableHeaderCell align="center">Action</TableHeaderCell>
            </TableRow>
          </TableHeader>

          <TableBody>
            {mealPeriods.map((p, i) => (
              <TableRow key={i}>
                <TableCell>
                  {isEditing ? (
                    <Input
                      className={classes.fixedWidthInput}
                      value={p.name}
                      placeholder="Meal period name"
                      onChange={(_, d) => updatePeriod(i, "name", d.value)}
                    />
                  ) : (
                    p.name || "Unnamed period"
                  )}
                </TableCell>

                <TableCell align="center">
                  {isEditing ? (
                    <Dropdown
                      aria-label={`Start time for ${p.name || "meal period"}`}
                      className={classes.fixedWidthInput}
                      value={getDropdownDisplayValue(p.startsOn)}
                      placeholder="--:--"
                      selectedOptions={p.startsOn ? [p.startsOn] : []}
                      onOptionSelect={(_, d) => updatePeriod(i, "startsOn", d.optionValue as string)}
                    >
                      {timeOptions.map((o) => (
                        <Option key={o.key} value={o.key}>
                          {o.text}
                        </Option>
                      ))}
                    </Dropdown>
                  ) : (
                    formatTimeToAMPM(p.startsOn) || "Not set"
                  )}
                </TableCell>

                <TableCell align="center">
                  {isEditing ? (
                    <Dropdown
                      aria-label={`End time for ${p.name || "meal period"}`}
                      className={classes.fixedWidthInput}
                      value={getDropdownDisplayValue(p.endsOn)}
                      placeholder="--:--"
                      selectedOptions={p.endsOn ? [p.endsOn] : []}
                      onOptionSelect={(_, d) => updatePeriod(i, "endsOn", d.optionValue as string)}
                    >
                      {timeOptions.map((o) => (
                        <Option key={o.key} value={o.key}>
                          {o.text}
                        </Option>
                      ))}
                    </Dropdown>
                  ) : (
                    formatTimeToAMPM(p.endsOn) || "Not set"
                  )}
                </TableCell>

                <TableCell align="center">
                  <Link
                    disabled={!isEditing}
                    aria-disabled={!isEditing}
                    aria-label={`Remove ${p.name || "meal period"}`}
                    onClick={isEditing ? () => removeMealPeriod(i) : undefined}
                    style={{ color: !isEditing ? tokens.colorNeutralForegroundDisabled : undefined }}
                  >
                    Remove meal period
                  </Link>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {isEditing && (
        <div className={classes.actions}>
          <Button appearance="primary" onClick={handleSave} disabled={!isSaveEnabled}>
            Save
          </Button>
          <Button onClick={handleCancel}>Cancel</Button>
        </div>
      )}
    </div>
  );
};

export default HoursOfOperation;
